import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';

import Analytics from '../../../../analytics/Analytics';
import useAlerts from '../../../../hooks/order/useAlerts';
import { modelOf } from '../../../../prop-types';
import AnalyticsService from '../../../../services/AnalyticsService';
import AccountStore from '../../../../store/AccountStore';
import CheckoutStore from '../../../../store/CheckoutStore';
import UIStore from '../../../../store/UIStore';
import CheckoutCartModule from '../../cart/CheckoutCartModule';
import CheckoutCustomerInfoModule from '../../customer/CheckoutCustomerInfoModule';
import CheckoutKCOModule from '../../payment/CheckoutKCOModule';
import CheckoutPaymentProviderModule from '../../payment/CheckoutPaymentProviderModule';
import CheckoutPaymentMethodModule from '../../payment/CheckoutPaymentMethodModule';
import CheckoutRecipientInfoModule from '../../recipient/CheckoutRecipientInfoModule';
import CheckoutShippingMethodModule from '../../shipping/CheckoutShippingMethodModule';
import CheckoutSummaryModule from '../../summary/CheckoutSummaryModule';
import OrderPaymentProvider from '../../../../types/order/OrderPaymentProvider';
import CheckoutCustomerCountryModule from '../../customer/CheckoutCustomerCountryModule';

const CheckoutPageModules = ({
  analytics,
  analyticsService,
  accountStore,
  checkoutStore,
  uiStore,
}) => {
  const { loggedIn } = accountStore;
  const { order } = checkoutStore;
  const {
    topOfPageAlerts,
    paymentProvider,
    shouldUseProviderCustomerHandling,
  } = order;
  const accountIsNotLoggedIn = !loggedIn;
  const cartIsNotEmpty = !order.cartIsEmpty;
  const isNotDesktop = !uiStore.isDesktop;

  const { renderErrorAlerts, renderGeneralAlerts } = useAlerts({
    errorAlerts: topOfPageAlerts.errors,
    generalAlerts: topOfPageAlerts.general,
  });

  const updateTimerRef = useRef(null);

  useEffect(() => {
    sendInitAnalyticEvents();
    return () => {
      disableTimer();
    };
  }, []);

  const sendGdprData = () => {
    order.gdpr_info && analytics.sendGdprData(order.gdpr_info);
  };

  const sendInitialPaymentInfo = () => {
    // Skip when cart is empty to avoid resolving selected_payment.method (can be stale/unresolvable)
    if (order.cartIsEmpty || !order?.productList?.length) {
      return;
    }
    // Send initial payment info if a payment method or provider is already selected
    const hasPaymentMethod = order?.selected_payment?.method;
    const hasPaymentProvider = order?.selected_payment?.provider;
    const hasProductsTotal = order?.productsTotal?.value != null;

    if ((hasPaymentMethod || hasPaymentProvider) && hasProductsTotal) {
      analyticsService.addPaymentInfo({
        products: order.productList,
        productsTotal: order.productsTotal.value,
      });
    }
  };

  const sendInitAnalyticEvents = () => {
    sendGdprData();
    sendInitialPaymentInfo();
  };

  const disableTimer = () => {
    if (updateTimerRef.current) {
      clearTimeout(updateTimerRef.current);
      updateTimerRef.current = null;
    }
  };

  const sendAnalyticsEvents = () => {
    analyticsService.removeFromCartCheckout();
  };

  const validateOrder = (args) => {
    checkoutStore.validateOrder(args).then(sendAnalyticsEvents);
  };

  const updateOrderWithTimer = (args) => {
    const { customTimer } = args;
    disableTimer();

    updateTimerRef.current = setTimeout(() => {
      validateOrder(args);
    }, customTimer || 2000);
  };

  const updateOrder = (args) => {
    const { withoutTimer } = args;
    if (withoutTimer) {
      validateOrder(args);
    } else {
      updateOrderWithTimer(args);
    }
  };

  // Payment provider defines the order in which modules are rendered.

  let moduleOrder;

  switch (paymentProvider) {
    case OrderPaymentProvider.KUSTOM:
      moduleOrder = [
        CheckoutPaymentProviderModule,
        CheckoutCartModule,
        CheckoutCustomerCountryModule,
        CheckoutShippingMethodModule,
        CheckoutKCOModule,
      ];
      break;
    case OrderPaymentProvider.PAYTRAIL:
    default:
      moduleOrder = [
        CheckoutPaymentProviderModule,
        CheckoutCustomerInfoModule,
        CheckoutCartModule,
        CheckoutRecipientInfoModule,
        CheckoutShippingMethodModule,
        CheckoutPaymentMethodModule,
      ];
      break;
  }

  const modules = [];

  // KCO replaces customer field and payment modules.
  for (const Module of moduleOrder) {
    const isModuleEnabled = (module) => {
      switch (module) {
        case CheckoutCartModule:
        case CheckoutKCOModule:
          return true;
        case CheckoutPaymentProviderModule:
          return order.canChangePaymentProvider && cartIsNotEmpty;
        case CheckoutCustomerCountryModule:
          return order.hasCountryOptions && cartIsNotEmpty;
        case CheckoutCustomerInfoModule:
          return (
            accountIsNotLoggedIn && order.hasCustomerFields && cartIsNotEmpty
          );
        case CheckoutRecipientInfoModule:
          return order.hasCustomerFields && cartIsNotEmpty;
        case CheckoutPaymentMethodModule:
        case CheckoutShippingMethodModule:
          return cartIsNotEmpty;
        default:
          return false;
      }
    };

    if (isModuleEnabled(Module)) {
      modules.push(Module);
    }
  }

  // Insert CheckoutSummaryModule after CheckoutCartModule in KCO non-desktop view
  if (shouldUseProviderCustomerHandling && isNotDesktop) {
    const cartModuleIndex = modules.indexOf(CheckoutCartModule);
    if (cartModuleIndex !== -1) {
      modules.splice(cartModuleIndex + 1, 0, CheckoutSummaryModule);
    }
  }

  return (
    <div className="CheckoutPageModules">
      {renderErrorAlerts()}
      {renderGeneralAlerts()}
      {modules.map((ModuleComponent, index) => (
        <ModuleComponent
          key={index}
          orderNumber={index + 1}
          onOrderUpdate={updateOrder}
        />
      ))}
    </div>
  );
};

CheckoutPageModules.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  analyticsService: PropTypes.instanceOf(AnalyticsService).isRequired,
};

export default inject(
  'analyticsService',
  'analytics',
  'accountStore',
  'checkoutStore',
  'uiStore'
)(observer(CheckoutPageModules));
